import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "scrollContainer",
    "badgeSection",
    "greetingSection",
    "inputSection",
    "generateButton",
    "queryInput",
    "chatHistorySection",
    "progressSection",
    "resultsSection",
    "modal",
    "modalContent",
    // Stage targets
    "stage1", "stage1Header", "stage1Result",
    "courseTypeLabel", "classificationType", "complexityLabel", "classificationComplexity",
    "stage2", "stage2Header", "stage2Result", "stage2Question", "questionText", "stage2Buttons", "stage2UpdateMessage",
    "stage3", "stage3Header", "stage3ReasoningContainer",
    "stage4", "stage4Header", "stage4ThinkingContainer",
    "stage5", "stage5Header",
    "stage6", "stage6Header", "stage6Result",
    // Results targets
    "planTitle", "planDuration", "planVideoCount", "planChapterCount",
    "planReasoning", "chaptersContainer",
    "countInModal"
  ]

  connect() {
    console.log("Professor Mode controller connected")
    this.currentStage = 0
    this.reasoningIndex = 0
    this.reasoningTimeouts = []
    this.userHasScrolledUp = false
    this.autoScrollEnabled = false
    // Configurable delay between video imports (in milliseconds)
    this.videoImportDelayMs = 200
    this.setupScrollMonitoring()
  }

  disconnect() {
    this.clearReasoningTimeouts()
  }

  toggleModal() {
    this.modalTarget.classList.toggle('hidden')
  }

  closeModalOnBackdrop(event) {
    // Only close if clicking the backdrop, not the modal content
    if (event.target === this.modalTarget) {
      this.toggleModal()
    }
  }

  clearReasoningTimeouts() {
    this.reasoningTimeouts.forEach(timeoutId => clearTimeout(timeoutId))
    this.reasoningTimeouts = []
  }

  setupScrollMonitoring() {
    // Monitor container scroll
    if (this.hasScrollContainerTarget) {
      this.scrollContainerTarget.addEventListener('scroll', () => {
        const scrollTop = this.scrollContainerTarget.scrollTop
        const scrollHeight = this.scrollContainerTarget.scrollHeight
        const clientHeight = this.scrollContainerTarget.clientHeight

        const isNearBottom = (scrollHeight - scrollTop - clientHeight) < 100

        if (!isNearBottom) {
          this.userHasScrolledUp = true
        } else {
          this.userHasScrolledUp = false
        }
      })
    }
  }

  scrollToBottom() {
    if (this.autoScrollEnabled && !this.userHasScrolledUp && this.hasScrollContainerTarget) {
      // Scroll the container, not the window
      this.scrollContainerTarget.scrollTo({ top: this.scrollContainerTarget.scrollHeight, behavior: 'smooth' })
    }
  }

  // Mock data for the prototype
  async typeInSampleQuery() {
    // Select a sample query that matches the mock data
    const sampleQuery = "I'm struggling to finish from north-south, can you help me build an attacking game from that position?"

    // Focus the textarea
    this.queryInputTarget.focus()

    // Clear any existing content (shouldn't be needed, but just in case)
    this.queryInputTarget.value = ''

    // Short wait before we start "typing"
    await new Promise(resolve => setTimeout(resolve, 300))

    // Type character by character
    for (let i = 0; i < sampleQuery.length; i++) {
      this.queryInputTarget.value += sampleQuery[i]

      // Variable delay for more natural typing
      const baseDelay = 80
      const variation = Math.random() * 40 - 20 // ±20ms variation
      const delay = baseDelay + variation

      await new Promise(resolve => setTimeout(resolve, delay))
    }

    // Wait a moment after typing is complete, then auto-submit
    await new Promise(resolve => setTimeout(resolve, 300))
    this.generatePlan()
  }

  getMockData() {
    return {
      classification: {
        type: "Short Course",
        complexity: "Intermediate",
        key_concepts: ["north-south position control", "submission attacks from north-south", "attack chaining", "positional pressure"]
      },
      userContext: {
        belt_level: "White belt | 3 Stripes | 2 years",
        gi_preference: "Gi-focused training",
        game_style: "Guard-based style",
        learning_style: "Systematic learner"
      },
      reasoningSnippets: [
        "Building systematic north-south attack knowledge",
        "Progressing from control fundamentals to submissions",
        "Expert instruction from world champions",
        "Emphasis on chaining attacks together",
        "Practical troubleshooting for common mistakes",
        "Comprehensive finishing details",
        "Structured progression for reliable finishes"
      ],
      plan: {
        title: "Complete North-South Attacking System",
        type: "short_course",
        estimated_duration: "1-2 weeks",
        video_count: 7,
        chapter_count: 3,
        plan_reasoning: [
          "Progression starts with foundational position control before introducing submissions, ensuring solid base for attacks",
          "Early chapters provide broad survey of submission options to build attacking mindset and recognize opportunities",
          "Core submissions (north-south choke and kimura) emphasized throughout with world-class instruction from Marcelo Garcia and others",
          "Advanced chapters focus on chaining attacks together to troubleshoot defensive reactions and create seamless finishing sequences",
          "Multiple instructor perspectives reinforce key concepts while showing different approaches to the same techniques",
          "Course structure ensures both gi and no-gi applicability, with emphasis on fundamental mechanics that translate across rulesets"
        ],
        chapters: [
          {
            number: 1,
            title: "North-South Control Fundamentals",
            description: "This chapter establishes foundational understanding of entering, stabilizing, and maintaining the north-south position. You'll learn essential control concepts and get a comprehensive survey of attacking options before diving into specific submissions.",
            learning_objectives: [
              "establish and maintain north-south control",
              "understand critical body positioning details",
              "recognize multiple submission opportunities",
              "develop systematic attacking mindset"
            ],
            estimated_duration_minutes: 14,
            videos: [
              {
                position: 1,
                id: "HW1lA2aUdiM",
                title: "North-South Position Basics and Transitions: BJJ Fundamentals with Kris Kim (Black Belt)",
                instructor: "Kris Kim",
                duration_minutes: 4,
                thumbnail_url: "https://i.ytimg.com/vi/HW1lA2aUdiM/hqdefault.jpg",
                selection_reasoning: "Starts the sequence by providing foundational concepts for entering, stabilizing, and maintaining north-south control. Understanding these mechanics is essential before focusing on submissions, ensuring a solid base to launch attacks and prevent escapes."
              },
              {
                position: 2,
                id: "i3_vevp6EP4",
                title: "My 3 Favorite North South Submission Attacks",
                instructor: "Stephan Kesting",
                duration_minutes: 6,
                thumbnail_url: "https://i.ytimg.com/vi/i3_vevp6EP4/hqdefault.jpg",
                selection_reasoning: "Stephan Kesting's expert instruction surveys three high-percentage submissions that bridge fundamental control (from video 1) to specific techniques. This video develops your attacking mindset by showing multiple finish options with clear setups and details, preparing you for more specialized instruction in subsequent videos."
              },
              {
                position: 3,
                id: "4UxP6INNM_Q",
                title: "Paper Cutter (Bread Cutter) Choke from North South Position – BJJ Gi Fundamentals",
                instructor: "Unknown",
                duration_minutes: 4,
                thumbnail_url: "https://i.ytimg.com/vi/4UxP6INNM_Q/hqdefault.jpg",
                selection_reasoning: "Rounds out your arsenal with a powerful submission option (paper cutter choke) from north-south, especially useful for gi players. Includes step-by-step entry, grip, and finishing details, expanding your submission versatility."
              }
            ]
          },
          {
            number: 2,
            title: "Core North-South Submissions",
            description: "Focus shifts to mastering the two most essential north-south submissions: the north-south choke and the kimura. These techniques form the cornerstone of your north-south attacking game and are applicable in both gi and no-gi.",
            learning_objectives: [
              "master north-south choke mechanics",
              "perfect kimura setups and finishes",
              "understand scorpion death lock details",
              "troubleshoot common defensive reactions"
            ],
            estimated_duration_minutes: 12,
            videos: [
              {
                position: 1,
                id: "x1cuQ9uZ_nk",
                title: "North-South Choke, Scorpion Death Lock & Kimura from North-South Position – Tum Energia",
                instructor: "Tum Energia, Storm ten Kate",
                duration_minutes: 12,
                thumbnail_url: "https://i.ytimg.com/vi/x1cuQ9uZ_nk/hqdefault.jpg",
                selection_reasoning: "Delivers clear instruction on key submissions (north-south choke, kimura, and the unique scorpion death lock), directly addressing finishing struggles with actionable details and troubleshooting, as well as transitions between submissions."
              }
            ]
          },
          {
            number: 3,
            title: "Attack Chains and Transitions",
            description: "Advanced chapter focusing on chaining attacks together and transitioning between submissions. Learn how world-class grapplers like Marcelo Garcia seamlessly flow between north-south choke, kimura, and armbar to create unstoppable finishing sequences.",
            learning_objectives: [
              "chain kimura to north-south choke",
              "transition from choke to armbar",
              "break stubborn defensive grips",
              "develop fluid attack sequences"
            ],
            estimated_duration_minutes: 20,
            videos: [
              {
                position: 1,
                id: "FT6TIlcM_VY",
                title: "Kimura to North South Choke Transition from Side Control – BJJ Setup and Finishing Details",
                instructor: "Chewy, Adam Wilson",
                duration_minutes: 6,
                thumbnail_url: "https://i.ytimg.com/vi/FT6TIlcM_VY/hqdefault.jpg",
                selection_reasoning: "Focuses on chaining the kimura and north-south choke, providing step-by-step guidance on threading attacks together and crucial details to troubleshoot failed finishes—key for building a fluid, resilient attack system from north-south."
              },
              {
                position: 2,
                id: "ExRy6o0fbAM",
                title: "Marcelo Garcia Teaches North South Choke and Armbar Transitions from Side Control",
                instructor: "Marcelo Garcia",
                duration_minutes: 11,
                thumbnail_url: "https://i.ytimg.com/vi/ExRy6o0fbAM/hqdefault.jpg",
                selection_reasoning: "World-class instruction on weight placement, submission chaining between north-south choke and armbar, and strategic adjustments. Marcelo's details on preventing escapes and leveraging transitions are invaluable for a high-level finishing game."
              },
              {
                position: 3,
                id: "cnfcBYYDYQY",
                title: "North South Triple Attack: Choke & Armbar Setups from Top with Kris Kim (BJJ Purple Belts)",
                instructor: "Kris Kim",
                duration_minutes: 3,
                thumbnail_url: "https://i.ytimg.com/vi/cnfcBYYDYQY/hqdefault.jpg",
                selection_reasoning: "Illustrates practical attack chaining—north-south choke, alternate side choke, and armbar—teaching you how to break stubborn defenses and transition smoothly between submissions as the opponent reacts, cementing an effective attack sequence."
              }
            ]
          }
        ]
      }
    }
  }

  async generatePlan() {
    const query = this.queryInputTarget.value.trim()

    if (!query) {
      // Instead of showing an error, populate with a sample query
      await this.typeInSampleQuery()
      return
    }

    // Save query for later use
    this.userQuery = query

    // Disable button and input to prevent changes
    this.generateButtonTarget.disabled = true
    this.queryInputTarget.disabled = true

    // Fade out badge, greeting and input sections immediately
    this.badgeSectionTarget.style.transition = 'opacity 400ms ease-out'
    this.greetingSectionTarget.style.transition = 'opacity 400ms ease-out'
    this.inputSectionTarget.style.transition = 'opacity 400ms ease-out'

    this.badgeSectionTarget.style.opacity = '0'
    this.greetingSectionTarget.style.opacity = '0'
    this.inputSectionTarget.style.opacity = '0'

    // After fade out (400ms), hide sections and show chat history
    setTimeout(() => {
      this.badgeSectionTarget.classList.add('hidden')
      this.greetingSectionTarget.classList.add('hidden')
      this.inputSectionTarget.classList.add('hidden')

      // Show chat history and create user message bubble
      this.chatHistorySectionTarget.classList.remove('hidden')
      this.createUserMessageBubble(query)

      // Pause 400ms, then show progress section and start stages
      setTimeout(() => {
        this.progressSectionTarget.classList.remove('hidden')
        this.currentStage = 0
        this.startStageProgression()
      }, 400)
    }, 400)
  }

  createUserMessageBubble(query) {
    const messageDiv = document.createElement('div')
    messageDiv.className = 'user-message'
    messageDiv.innerHTML = `
      <div class="user-message-bubble">
        ${this.escapeHtml(query)}
      </div>
    `
    this.chatHistorySectionTarget.appendChild(messageDiv)
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  startStageProgression() {
    // Enable auto-scroll for the generation process
    this.autoScrollEnabled = true
    this.userHasScrolledUp = false

    let delaySinceLoad = 0;

    // Stage 1: Classification (~2400ms actual duration)
    setTimeout(() => this.showStage1(), delaySinceLoad)

    // Wait for Stage 1 to complete (400ms for local DB fetch)
    delaySinceLoad += 2000
    setTimeout(() => this.completeStage1(), delaySinceLoad)

    // Stage 2: User Context (~3600ms actual duration)
    delaySinceLoad += 1600
    setTimeout(() => this.transitionToStage2(), delaySinceLoad)

    // Wait for Stage 2 to complete async work to START (4000ms buffer)
    delaySinceLoad += 1000
    setTimeout(() => this.completeStage2(), delaySinceLoad)

    // NOTE: Stage 3 and beyond are now triggered by user interaction (handleGiPreferenceSelection)
    // The progression continues in continueToStage3() after the user selects their preference
  }

  // Helper methods for stage transitions
  showStageElement(stageTarget) {
    stageTarget.classList.remove('hidden')
    // Trigger reflow to enable transition
    stageTarget.offsetHeight
    stageTarget.classList.remove('opacity-0')
    stageTarget.classList.add('opacity-100')
  }

  hideStageElement(stageTarget) {
    stageTarget.classList.remove('opacity-100')
    stageTarget.classList.add('opacity-0')
    // Hide after transition completes
    setTimeout(() => {
      stageTarget.classList.add('hidden')
    }, 300)
  }

  // Split text into token-like chunks (~4 characters per token)
  smartTokenize(text) {
    const tokens = []
    const words = text.split(/(\s+)/)

    for (const word of words) {
      if (/^\s+$/.test(word)) {
        // Preserve whitespace as separate tokens
        tokens.push(word)
      } else if (word.length <= 6) {
        // Short words stay whole
        tokens.push(word)
      } else {
        // Split longer words into ~4-char chunks
        let remaining = word
        while (remaining.length > 0) {
          const chunkSize = Math.min(4, remaining.length)
          tokens.push(remaining.slice(0, chunkSize))
          remaining = remaining.slice(chunkSize)
        }
      }
    }

    return tokens
  }

  // Stream text token-by-token for realistic LLM-like progressive disclosure
  async streamText(element, text, baseDelayMs = 50) {
    element.textContent = ''

    // Split into token-like chunks
    const tokens = this.smartTokenize(text)

    for (const token of tokens) {
      // Variable delay based on token characteristics
      let delay = baseDelayMs

      // Shorter delays for whitespace
      if (/^\s+$/.test(token)) {
        delay = baseDelayMs * 0.3
      }
      // Slightly longer for punctuation
      else if (/^[.,!?;:()\-]+$/.test(token)) {
        delay = baseDelayMs * 0.5
      }

      await new Promise(resolve => setTimeout(resolve, delay))
      element.textContent += token

      // Keep content visible as it streams
      this.scrollToBottom()
    }
  }

  // Calculate total streaming duration for a text string
  calculateStreamDuration(text, delayMs = 50) {
    const tokenCount = this.smartTokenize(text).length
    return tokenCount * delayMs
  }

  // Stage 1: Classification
  async showStage1() {
    // Show container and fade in
    this.showStageElement(this.stage1Target)

    // Add pulsing animation to stage arrow
    const indicator = this.stage1HeaderTarget.querySelector('.stage-indicator')
    indicator.classList.add('pulsing-arrow')

    // Scroll into view
    this.stage1Target.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }

  async completeStage1() {
    const mockData = this.getMockData()

    // Change stage indicator from → to ✓
    const indicator = this.stage1HeaderTarget.querySelector('.stage-indicator')
    indicator.classList.remove('pulsing-arrow')
    indicator.textContent = '✓'
    indicator.classList.remove('text-purple-400')
    indicator.classList.add('text-green-500')

    // Show result container
    this.stage1ResultTarget.classList.remove('hidden')

    // Stream "Course Type:" label
    await this.streamText(this.courseTypeLabelTarget, 'Course Type:')

    // Pause to let label appear
    await new Promise(resolve => setTimeout(resolve, 200))

    // Stream course type value
    await this.streamText(this.classificationTypeTarget, mockData.classification.type)

    // Pause between fields
    await new Promise(resolve => setTimeout(resolve, 200))

    // Stream "Complexity:" label
    await this.streamText(this.complexityLabelTarget, 'Complexity:')

    // Pause to let label appear
    await new Promise(resolve => setTimeout(resolve, 200))

    // Stream complexity value
    await this.streamText(this.classificationComplexityTarget, mockData.classification.complexity)
  }

  // Stage 2: User Context
  async transitionToStage2() {
    // Don't hide Stage 1 - keep it visible

    this.showStageElement(this.stage2Target)

    // Add pulsing animation to stage arrow
    const indicator = this.stage2HeaderTarget.querySelector('.stage-indicator')
    indicator.classList.add('pulsing-arrow')

    // Scroll into view
    this.stage2Target.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }

  async completeStage2() {
    const mockData = this.getMockData()

    // Change stage indicator from → to ✓
    const indicator = this.stage2HeaderTarget.querySelector('.stage-indicator')
    indicator.classList.remove('pulsing-arrow')
    indicator.textContent = '✓'
    indicator.classList.remove('text-purple-400')
    indicator.classList.add('text-green-500')

    // Show result container
    this.stage2ResultTarget.classList.remove('hidden')

    // Prepare container for streaming items
    const container = this.stage2ResultTarget.querySelector('.grid')
    container.innerHTML = ''

    // Stream in each user context item progressively
    const contextItems = [
      mockData.userContext.belt_level,
      mockData.userContext.gi_preference,
      mockData.userContext.game_style,
      mockData.userContext.learning_style
    ]

    for (const item of contextItems) {
      // Create item element
      const itemDiv = document.createElement('div')
      itemDiv.className = 'flex items-center'
      itemDiv.innerHTML = `
        <svg class="h-3 w-3 text-purple-400 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
        </svg>
        <span></span>
      `
      container.appendChild(itemDiv)

      // Stream the text into the span
      const span = itemDiv.querySelector('span')
      await this.streamText(span, item)

      // Pause between items to make each one visible
      await new Promise(resolve => setTimeout(resolve, 100))
    }

    // After streaming all context items, show the interactive question
    await new Promise(resolve => setTimeout(resolve, 800))

    // Show question section (but hide buttons initially)
    this.stage2QuestionTarget.classList.remove('hidden')
    this.stage2ButtonsTarget.classList.add('opacity-0')

    // Stream in the question text
    await this.streamText(this.questionTextTarget, 'Do you train Gi, No-Gi or Both?')

    // Wait 200ms after question text finishes before showing buttons
    await new Promise(resolve => setTimeout(resolve, 200))

    // Show buttons with fade-in
    this.stage2ButtonsTarget.classList.remove('opacity-0')
    this.stage2ButtonsTarget.classList.add('transition-opacity', 'duration-300')

    // Scroll to make buttons visible
    this.scrollToBottom()

    // Now wait for user interaction - progression continues in handleGiPreferenceSelection
  }

  async handleGiPreferenceSelection(event) {
    const selectedPreference = event.currentTarget.dataset.preference
    const selectedButton = event.currentTarget

    // Disable all buttons to prevent multiple clicks
    const allButtons = this.stage2ButtonsTarget.querySelectorAll('button')
    allButtons.forEach(btn => btn.disabled = true)

    // Update selected button style
    selectedButton.classList.remove('bg-gray-700', 'hover:bg-purple-900', 'border-gray-600')
    selectedButton.classList.add('bg-purple-900', 'border-purple-500')

    // Add checkmark to selected button
    selectedButton.innerHTML = `
      <div class="flex items-center gap-1">
        <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
        </svg>
        <span>${selectedPreference}</span>
      </div>
    `

    // Wait a moment to show the selection
    await new Promise(resolve => setTimeout(resolve, 300))

    // Hide unselected buttons
    allButtons.forEach(btn => {
      if (btn !== selectedButton) {
        btn.style.transition = 'opacity 300ms ease-out'
        btn.style.opacity = '0'
        setTimeout(() => btn.classList.add('hidden'), 300)
      }
    })

    // Show "Updating user profile..." message
    await new Promise(resolve => setTimeout(resolve, 400))
    this.stage2UpdateMessageTarget.classList.remove('hidden')
    this.scrollToBottom()

    // Simulate profile update
    await new Promise(resolve => setTimeout(resolve, 1500))

    // Hide the updating message and replace with checkmark
    const messageIcon = this.stage2UpdateMessageTarget.querySelector('.message-icon')
    messageIcon.innerHTML = '✓'
    messageIcon.classList.remove('text-purple-400')
    messageIcon.classList.add('text-green-500')

    // Continue with stage progression
    await new Promise(resolve => setTimeout(resolve, 500))
    this.continueToStage3()
  }

  async continueToStage3() {
    // Run stages sequentially, each waiting for the previous to complete
    await this.runStage3()
    await this.runStage4()
    await this.showChapterOverview()
    await this.runStage5()
    await this.showResults()
  }

  // Wrapper methods that run each stage to completion
  async runStage3() {
    await this.transitionToStage3()
    await this.completeStage3()
  }

  async runStage4() {
    await this.transitionToStage4()
    await this.completeStage4()
  }

  async runStage5() {
    await this.transitionToStage5()
    await new Promise(resolve => setTimeout(resolve, 2500))
    this.completeStage5()
  }

  // Stage 3: Searching for Relevant Content (formerly Stage 4)
  async transitionToStage3() {
    // Don't hide Stage 2 - keep it visible

    this.showStageElement(this.stage3Target)

    // Add pulsing animation to stage arrow
    const indicator = this.stage3HeaderTarget.querySelector('.stage-indicator')
    indicator.classList.add('pulsing-arrow')

    // Scroll into view
    this.stage3Target.scrollIntoView({ behavior: 'smooth', block: 'nearest' })

    // Wait for video search to complete
    await this.startVideoSearch()
  }

  async startVideoSearch() {
    const searchMessages = [
      { message: "Searching YouTube...", duration: 3500, result: "Found 1,045 videos" },
      { message: "Searching Vimeo...", duration: 1500, result: "Found 23 videos" },
      { message: "Searching custom video library...", duration: 1500, result: "Found 16 videos" }
    ]

    // Process each search message
    for (const item of searchMessages) {
      // Create message wrapper (block container for message + result)
      const messageWrapper = document.createElement('div')
      messageWrapper.className = 'flex flex-col gap-1'

      // Create message row with icon and text
      const messageRow = document.createElement('div')
      messageRow.className = 'flex items-start gap-2 text-sm text-gray-400 italic'
      messageRow.innerHTML = `
        <span class="message-icon mt-0.5 text-purple-400">
          <svg class="spinner" viewBox="0 0 50 50">
            <circle class="spinner-circle" cx="25" cy="25" r="20"></circle>
          </svg>
        </span>
        <span class="message-text"></span>
      `

      messageWrapper.appendChild(messageRow)
      this.stage3ReasoningContainerTarget.appendChild(messageWrapper)

      // Stream text into the message
      const textSpan = messageRow.querySelector('.message-text')
      await this.streamText(textSpan, item.message)

      // Keep spinner visible for the configured duration
      await new Promise(resolve => setTimeout(resolve, item.duration))

      // Replace spinner with checkmark
      const iconSpan = messageRow.querySelector('.message-icon')
      iconSpan.innerHTML = '✓'
      iconSpan.className = 'message-icon mt-0.5 text-green-500'

      // Stream the result text (faster streaming with 30ms delay)
      const resultDiv = document.createElement('div')
      resultDiv.className = 'flex items-start gap-2 text-xs text-gray-500 ml-7'
      resultDiv.innerHTML = `
        <span class="mt-0.5">↳</span>
        <span class="result-text"></span>
      `
      messageWrapper.appendChild(resultDiv)

      const resultSpan = resultDiv.querySelector('.result-text')
      await this.streamText(resultSpan, item.result, 30)

      // Wait 200ms before next message
      await new Promise(resolve => setTimeout(resolve, 200))
    }
  }

  async completeStage3() {
    // Change stage indicator from → to ✓
    const indicator = this.stage3HeaderTarget.querySelector('.stage-indicator')
    indicator.classList.remove('pulsing-arrow')
    indicator.textContent = '✓'
    indicator.classList.remove('text-purple-400')
    indicator.classList.add('text-green-500')
  }

  // Stage 4: Designing Your Course (formerly Stage 3)
  async transitionToStage4() {
    // Don't hide Stage 3 - keep it visible

    this.showStageElement(this.stage4Target)

    // Add pulsing animation to stage arrow
    const indicator = this.stage4HeaderTarget.querySelector('.stage-indicator')
    indicator.classList.add('pulsing-arrow')

    // Scroll into view
    this.stage4Target.scrollIntoView({ behavior: 'smooth', block: 'nearest' })

    // Wait for course design to complete
    await this.startCourseDesign()
  }

  async startCourseDesign() {
    const designMessages = [
      "Reviewing available content",
      "Building course structure",
      "Finding videos that match your belt and style"
    ]

    // Process each design message
    for (const message of designMessages) {
      // Create message div with icon and text placeholders
      const messageDiv = document.createElement('div')
      messageDiv.className = 'flex items-start gap-2 text-sm text-gray-400 italic'
      messageDiv.innerHTML = `
        <span class="message-icon mt-0.5 text-purple-400">
          <svg class="spinner" viewBox="0 0 50 50">
            <circle class="spinner-circle" cx="25" cy="25" r="20"></circle>
          </svg>
        </span>
        <span class="message-text"></span>
      `

      this.stage4ThinkingContainerTarget.appendChild(messageDiv)

      // Stream text into the message
      const textSpan = messageDiv.querySelector('.message-text')
      await this.streamText(textSpan, message)

      // Keep spinner visible for a moment
      await new Promise(resolve => setTimeout(resolve, 1500))

      // Replace spinner with checkmark
      const iconSpan = messageDiv.querySelector('.message-icon')
      iconSpan.innerHTML = '✓'
      iconSpan.className = 'message-icon mt-0.5 text-green-500'

      // Pause before next message to let user absorb
      await new Promise(resolve => setTimeout(resolve, 800))
    }
  }

  async completeStage4() {
    // Change stage indicator from → to ✓
    const indicator = this.stage4HeaderTarget.querySelector('.stage-indicator')
    indicator.classList.remove('pulsing-arrow')
    indicator.textContent = '✓'
    indicator.classList.remove('text-purple-400')
    indicator.classList.add('text-green-500')
  }

  // Chapter Overview (personable section, not a stage)
  async showChapterOverview() {
    const mockData = this.getMockData()
    const chapters = mockData.plan.chapters

    // Wait a moment after stage 4 completes
    await new Promise(resolve => setTimeout(resolve, 1000))

    // Create a new container div for the chapter overview (inserted after stage 4)
    const overviewContainer = document.createElement('div')
    overviewContainer.className = 'mb-6'
    overviewContainer.setAttribute('data-chapter-overview', 'true')

    // Insert after stage 4 in the DOM
    this.stage4Target.parentNode.insertBefore(overviewContainer, this.stage5Target)

    // Scroll into view
    overviewContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' })

    // Get/create the container for content
    const chapterContainer = document.createElement('div')
    chapterContainer.className = 'text-sm text-gray-300 space-y-2'
    overviewContainer.appendChild(chapterContainer)

    // Create personable intro text element
    const introDiv = document.createElement('div')
    introDiv.className = 'text-gray-200 mb-6 text-sm'
    chapterContainer.appendChild(introDiv)

    // Stream the personable intro text
    const learnGoal = "develop a strong attacking game from north-south"
    const introText = `Awesome, I've put together a course that will help you ${learnGoal}. Here's an overview:`
    await this.streamText(introDiv, introText, 40)

    // Wait a moment before showing chapters
    await new Promise(resolve => setTimeout(resolve, 600))

    // Stream each chapter with its videos
    for (const chapter of chapters) {
      // Create chapter container
      const chapterSection = document.createElement('div')
      chapterSection.className = 'mb-4'
      chapterContainer.appendChild(chapterSection)

      // Create chapter title
      const chapterTitle = document.createElement('div')
      chapterTitle.className = 'text-gray-100 font-semibold mb-2 text-base'
      chapterSection.appendChild(chapterTitle)

      // Stream chapter title
      const chapterText = chapter.title
      await this.streamText(chapterTitle, chapterText, 40)

      // Wait a moment before showing videos
      await new Promise(resolve => setTimeout(resolve, 300))

      // Create videos container
      const videosContainer = document.createElement('div')
      videosContainer.className = 'ml-4 space-y-1'
      chapterSection.appendChild(videosContainer)

      // Show each video instantly with fade-in animation
      for (const video of chapter.videos) {
        const videoDiv = document.createElement('div')
        videoDiv.className = 'text-gray-400 text-sm flex items-start gap-2 opacity-0 transition-opacity duration-300 mb-2'
        videosContainer.appendChild(videoDiv)

        // Add bullet point
        const bullet = document.createElement('span')
        bullet.className = 'flex-shrink-0'
        bullet.textContent = `${video.position}.`
        videoDiv.appendChild(bullet)

        // Add video text
        const textSpan = document.createElement('span')
        videoDiv.appendChild(textSpan)

        const videoText = `${video.title} - ${video.instructor}`
        textSpan.textContent = videoText

        // Trigger fade-in animation
        requestAnimationFrame(() => {
          videoDiv.classList.remove('opacity-0')
        })

        // Keep content visible as videos appear
        this.scrollToBottom()

        // Variable delay between videos (100-300ms)
        const delay = 200 + Math.random() * 300
        await new Promise(resolve => setTimeout(resolve, delay))
      }

      // Pause between chapters
      await new Promise(resolve => setTimeout(resolve, 600))
    }

    // Scroll to show all chapters
    this.scrollToBottom()
  }

  // Stage 5: Narrative
  async transitionToStage5() {
    // Don't hide Stage 4 - keep it visible

    this.showStageElement(this.stage5Target)

    // Add pulsing animation to stage arrow
    const indicator = this.stage5HeaderTarget.querySelector('.stage-indicator')
    indicator.classList.add('pulsing-arrow')

    // Scroll into view
    this.stage5Target.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }

  completeStage5() {
    // Change stage indicator from → to ✓
    const indicator = this.stage5HeaderTarget.querySelector('.stage-indicator')
    indicator.classList.remove('pulsing-arrow')
    indicator.textContent = '✓'
    indicator.classList.remove('text-purple-400')
    indicator.classList.add('text-green-500')
  }

  // Show completion message (replacing stage 6 with personable text)
  async showResults() {
    const mockData = this.getMockData()
    const plan = mockData.plan

    // Don't hide previous stages - keep entire timeline visible

    // Show stage 6 (just the content, no header)
    this.showStageElement(this.stage6Target)

    // Scroll into view
    this.stage6Target.scrollIntoView({ behavior: 'smooth', block: 'nearest' })

    // Wait a moment before showing content
    await new Promise(resolve => setTimeout(resolve, 800))

    // Show result with completion message
    this.stage6ResultTarget.classList.remove('hidden')

    // Get the completion message container
    const completionContainer = this.stage6ResultTarget

    // Create personable completion message
    const messageDiv = document.createElement('div')
    messageDiv.className = 'text-gray-200 text-sm mb-3'
    completionContainer.appendChild(messageDiv)

    // Stream personable completion text
    const completionText = "Your custom course is ready and waiting!"
    await this.streamText(messageDiv, completionText, 50)

    // Wait a moment before showing card
    await new Promise(resolve => setTimeout(resolve, 400))

    // Create course summary card
    const summaryCard = document.createElement('div')
    summaryCard.className = 'bg-gradient-to-r from-purple-900/40 to-purple-800/40 border border-purple-700/50 rounded-lg p-4 mb-3'
    summaryCard.innerHTML = `
      <h3 class="text-base font-semibold text-white mb-3">
        ${plan.title}
      </h3>
      <div class="flex gap-4 text-sm text-gray-300">
        <div class="flex items-center">
          <svg class="h-4 w-4 mr-1.5 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z"/>
          </svg>
          <span>${plan.video_count} videos</span>
        </div>
        <div class="flex items-center">
          <svg class="h-4 w-4 mr-1.5 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z"/>
          </svg>
          <span>${plan.chapter_count} chapters</span>
        </div>
      </div>
    `
    completionContainer.appendChild(summaryCard)

    // Create View Course button (full width)
    const ctaButton = document.createElement('button')
    ctaButton.className = 'w-full bg-purple-700 hover:bg-purple-600 text-white text-sm font-medium py-2.5 px-4 rounded-lg transition-colors'
    ctaButton.textContent = 'View Your Course'
    ctaButton.onclick = () => this.viewCourse()
    completionContainer.appendChild(ctaButton)

    // Scroll to show the button
    this.scrollToBottom()

    // Now disable auto-scroll - generation is complete
    this.autoScrollEnabled = false
  }

  // Show full course details
  async viewCourse() {
    const mockData = this.getMockData()
    const plan = mockData.plan

    // Step 1: Fade out current content
    this.progressSectionTarget.classList.add('fade-out')
    this.chatHistorySectionTarget.classList.add('fade-out')

    // Wait for fade out to complete (400ms)
    await new Promise(resolve => setTimeout(resolve, 400))

    // Step 2: Hide faded content and scroll to top instantly (no transition)
    this.progressSectionTarget.classList.add('hidden')
    this.chatHistorySectionTarget.classList.add('hidden')

    if (this.hasScrollContainerTarget) {
      this.scrollContainerTarget.scrollTo({ top: 0, behavior: 'auto' })
    }

    // Step 3: Wait 300ms before revealing new content
    await new Promise(resolve => setTimeout(resolve, 300))

    // Step 4: Populate the results content
    this.planTitleTarget.textContent = plan.title
    this.planDurationTarget.textContent = plan.estimated_duration
    this.planVideoCountTarget.textContent = `${plan.video_count} videos`
    this.planChapterCountTarget.textContent = `${plan.chapter_count} chapters`

    // Populate plan reasoning
    this.planReasoningTarget.innerHTML = plan.plan_reasoning
      .map(reason => `<li class="flex items-start"><svg class="h-5 w-5 text-purple-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg><span>${reason}</span></li>`)
      .join('')

    // Populate chapters
    this.chaptersContainerTarget.innerHTML = plan.chapters.map(chapter => this.renderChapter(chapter)).join('')

    // Show results section with fade-in animation
    this.resultsSectionTarget.classList.remove('hidden')
    this.resultsSectionTarget.classList.add('fade-in')

    // Clean up fade classes after animation completes
    setTimeout(() => {
      this.progressSectionTarget.classList.remove('fade-out')
      this.chatHistorySectionTarget.classList.remove('fade-out')
      this.resultsSectionTarget.classList.remove('fade-in')
    }, 400)
  }

  renderChapter(chapter) {
    // Format duration from minutes to HH:MM:SS
    const formatDuration = (minutes) => {
      const totalSeconds = minutes * 60
      const hours = Math.floor(totalSeconds / 3600)
      const mins = Math.floor((totalSeconds % 3600) / 60)
      const secs = totalSeconds % 60
      return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
    }

    const videosHtml = chapter.videos.map((video, index) => `
      <div class="bg-gray-800 p-3 rounded-xl mb-4 last:mb-0">
        <div class="search-result__thumbnail">
          <img src="${video.thumbnail_url}" alt="${video.title}" class="search-result__thumbnail-image">
          <div class="text-xs font-extralight absolute right-[10px] bottom-[10px] text-white bg-black opacity-80 p-1 rounded-md">
            ${formatDuration(video.duration_minutes)}
          </div>
        </div>

        <div class="pt-2">
          <div class="text-base md:text-lg font-medium text-white md:pr-5">
            ${index + 1}. ${video.title}
          </div>
          <div class="text-sm text-gray-300 mt-1">
            ${video.instructor}
          </div>

          <details class="text-sm mt-3">
            <summary class="cursor-pointer text-purple-400 hover:text-purple-300 font-medium">
              Why this video?
            </summary>
            <p class="mt-2 text-gray-300 pl-4 border-l-2 border-purple-700 italic font-light">
              ${video.selection_reasoning}
            </p>
          </details>
        </div>
      </div>
    `).join('')

    const objectivesHtml = chapter.learning_objectives
      .map(obj => `<li class="flex items-start"><svg class="h-4 w-4 text-purple-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg><span>${obj}</span></li>`)
      .join('')

    return `
      <div class="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
        <!-- Chapter Header -->
        <div class="bg-gray-800 border-b border-gray-700 px-6 py-4">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <div class="flex items-center mb-2">
                <span class="inline-flex items-center justify-center w-8 h-8 rounded-full bg-purple-900 text-white font-semibold text-sm mr-3">
                  ${chapter.number}
                </span>
                <h3 class="font-bold text-xl text-white">${chapter.title}</h3>
              </div>
              <p class="text-gray-300 text-sm mb-3 ml-11 font-light">${chapter.description}</p>
              <div class="ml-11 text-sm text-gray-400 font-light">
                <span class="inline-flex items-center">
                  <svg class="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
                  </svg>
                  ${chapter.estimated_duration_minutes} minutes
                </span>
                <span class="mx-2">•</span>
                <span>${chapter.videos.length} videos</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Learning Objectives -->
        <div class="px-6 py-4 bg-gray-800 border-b border-gray-700">
          <h4 class="font-semibold text-sm text-purple-300 mb-2">Learning Objectives:</h4>
          <ul class="space-y-1 text-sm text-gray-300 font-light">
            ${objectivesHtml}
          </ul>
        </div>

        <!-- Videos -->
        <div class="p-2 bg-gray-900">
          ${videosHtml}
        </div>

        <!-- Chapter Actions -->
        <div class="px-6 py-4 bg-gray-800 border-t border-gray-700">
          <button
            data-action="click->professor-mode#startChapter"
            data-chapter-number="${chapter.number}"
            class="w-full md:w-auto px-6 py-2 bg-purple-900 hover:bg-purple-800 text-white font-semibold rounded-lg transition-colors">
            Start Chapter ${chapter.number}
          </button>
        </div>
      </div>
    `
  }

  reset() {
    // Reset to initial state
    this.autoScrollEnabled = false
    this.userHasScrolledUp = false

    this.badgeSectionTarget.classList.remove('hidden')
    this.badgeSectionTarget.style.transition = ''
    this.badgeSectionTarget.style.opacity = '1'

    this.greetingSectionTarget.classList.remove('hidden')
    this.greetingSectionTarget.style.transition = ''
    this.greetingSectionTarget.style.opacity = '1'

    this.inputSectionTarget.classList.remove('hidden')
    this.inputSectionTarget.style.transition = ''
    this.inputSectionTarget.style.opacity = '1'

    // Reset button and input state
    this.generateButtonTarget.disabled = false
    this.queryInputTarget.disabled = false
    // Remove any spinner overlays
    const spinnerOverlay = this.generateButtonTarget.querySelector('.animate-spin')
    if (spinnerOverlay) {
      spinnerOverlay.remove()
    }

    this.progressSectionTarget.classList.add('hidden')
    this.resultsSectionTarget.classList.add('hidden')

    // Hide and clear chat history
    this.chatHistorySectionTarget.classList.add('hidden')
    this.chatHistorySectionTarget.innerHTML = ''

    // Reset all stages (opacity-0, no hidden)
    this.stage1Target.classList.add('opacity-0')
    this.stage1Target.classList.remove('opacity-100')
    this.stage2Target.classList.add('opacity-0')
    this.stage2Target.classList.remove('opacity-100')
    this.stage3Target.classList.add('opacity-0')
    this.stage3Target.classList.remove('opacity-100')
    this.stage4Target.classList.add('opacity-0')
    this.stage4Target.classList.remove('opacity-100')
    this.stage5Target.classList.add('opacity-0')
    this.stage5Target.classList.remove('opacity-100')
    this.stage6Target.classList.add('opacity-0')
    this.stage6Target.classList.remove('opacity-100')

    // Reset stage indicators (→ instead of ✓)
    this.resetStageIndicator('stage1')
    this.resetStageIndicator('stage2')
    this.resetStageIndicator('stage3')
    this.resetStageIndicator('stage4')
    this.resetStageIndicator('stage5')
    // Stage 6 has no visible indicator, so no need to reset it

    // Hide stage results
    this.stage1ResultTarget.classList.add('hidden')
    this.stage2ResultTarget.classList.add('hidden')
    this.stage6ResultTarget.classList.add('hidden')

    // Clear stage 1 content
    this.courseTypeLabelTarget.textContent = ''
    this.classificationTypeTarget.textContent = ''
    this.complexityLabelTarget.textContent = ''
    this.classificationComplexityTarget.textContent = ''

    // Reset stage 2 question section
    this.stage2QuestionTarget.classList.add('hidden')
    this.questionTextTarget.textContent = ''
    this.stage2UpdateMessageTarget.classList.add('hidden')

    // Reset all buttons to initial state
    const allButtons = this.stage2ButtonsTarget.querySelectorAll('button')
    allButtons.forEach((btn, index) => {
      btn.disabled = false
      btn.classList.remove('hidden', 'bg-purple-900', 'border-purple-500')
      btn.classList.add('bg-gray-700', 'hover:bg-purple-900', 'border-gray-600')
      btn.style.transition = ''
      btn.style.opacity = '1'

      // Restore original button text
      const preferences = ['Gi', 'No-Gi', 'Both']
      btn.innerHTML = preferences[index]
    })

    // Clear reasoning timeouts
    this.clearReasoningTimeouts()

    // Clear stage 3 reasoning container
    this.stage3ReasoningContainerTarget.innerHTML = ''

    // Clear stage 4 thinking container
    this.stage4ThinkingContainerTarget.innerHTML = ''

    // Clear stage 6 completion content
    this.stage6ResultTarget.innerHTML = ''

    // Remove dynamically created chapter overview container
    const chapterOverview = this.progressSectionTarget.querySelector('[data-chapter-overview]')
    if (chapterOverview) {
      chapterOverview.remove()
    }

    // Scroll container to top
    if (this.hasScrollContainerTarget) {
      this.scrollContainerTarget.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  resetStageIndicator(stageName) {
    const header = this[`${stageName}HeaderTarget`]
    const indicator = header.querySelector('.stage-indicator')
    indicator.textContent = '→'
    indicator.classList.remove('text-green-500')
    indicator.classList.add('text-purple-400')
  }

  startChapter(event) {
    const chapterNumber = event.currentTarget.dataset.chapterNumber
    console.log(`Starting chapter ${chapterNumber}`)

    // Get the count-in modal controller
    const countInModalElement = this.countInModalTarget
    const countInController = this.application.getControllerForElementAndIdentifier(
      countInModalElement,
      'count-in-modal'
    )

    if (countInController) {
      // Show the modal and start countdown
      countInController.showModalAndStart()
    }
  }
}
